import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../../shared/config/src';
import { doc, getDoc } from 'firebase/firestore';

// --- TYPES & CONSTANTS ---

interface RadarAxes {
  ethical_balance: number;
  personalization: number;
  wisdom: number;
  activity: number;
  sleep: number;
  concentration: number;
  energy: number;
  self_esteem: number;
  emotional_clarity: number;
}

export interface WeeklyRadarScore {
  user_id: string;
  week_start: string;
  axes: RadarAxes;
  source_tags?: string[];
  timestamp: string;
  is_synthetic?: boolean;
}

export const RADAR_AXES = Object.keys({
  ethical_balance: 0, personalization: 0, wisdom: 0, activity: 0, sleep: 0,
  concentration: 0, energy: 0, self_esteem: 0, emotional_clarity: 0
}) as (keyof RadarAxes)[];

export const AXIS_LABELS: Record<keyof RadarAxes, string> = {
  ethical_balance: '⚖️ Balance', 
  personalization: '🧠 Personalization',
  wisdom: '🦉 Wisdom', 
  activity: '🏃‍♂️ Activity', 
  sleep: '😴 Sleep',
  concentration: '🎯 Focus', 
  energy: '🔋 Energy', 
  self_esteem: '💪 Self-Esteem',
  emotional_clarity: '💧 Clarity'
};

interface RadarChartContextType {
  radarData: WeeklyRadarScore | null;
  loading: boolean;
  error: string | null;
  isPatient?: boolean;
  requiresConsent?: boolean;
  grantConsent?: () => Promise<void>;
}

// --- HELPER FUNCTIONS ---

const generateSyntheticData = (): WeeklyRadarScore => {
  const axes = RADAR_AXES.reduce((acc, axis) => {
    acc[axis] = 50;
    return acc;
  }, {} as RadarAxes);

  return {
    user_id: 'synthetic',
    week_start: new Date().toISOString(),
    timestamp: new Date().toISOString(),
    axes,
    is_synthetic: true
  };
};

// --- CONTEXT ---

const RadarChartContext = createContext<RadarChartContextType | undefined>(undefined);

export const useRadarChart = () => {
  const context = useContext(RadarChartContext);
  if (context === undefined) {
    throw new Error('useRadarChart must be used within a RadarChartProvider');
  }
  return context;
};

// --- PROVIDER ---

export const RadarChartProvider: React.FC<{ 
  children: React.ReactNode; 
  patientId?: string;
  enableConsent?: boolean;
  onGrantConsent?: () => Promise<void>;
  settings?: any;
}> = ({ children, patientId, enableConsent = false, onGrantConsent, settings }) => {
  const { user } = useAuth();
  const [radarData, setRadarData] = useState<WeeklyRadarScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresConsent, setRequiresConsent] = useState(false);
  
  const isPatient = user?.role === 'patient';

  const grantConsent = async () => {
    if (onGrantConsent) {
      try {
        await onGrantConsent();
        setRequiresConsent(false);
      } catch (err) {
        console.error("Failed to save consent:", err);
        setError("Could not save your consent preference. Please try again.");
      }
    }
  };

  useEffect(() => {
    const fetchRadarData = async () => {
      setLoading(true);
      const targetUserId = patientId || user?.uid;

      if (!targetUserId) {
        setLoading(false);
        setError("No user specified for radar chart.");
        return;
      }

      // Check for consent if enabled and viewing own data
      if (enableConsent && !patientId && settings && !settings.consents?.symbolicRadarEnabled) {
        setRequiresConsent(true);
        setLoading(false);
        return;
      }
      
      setRequiresConsent(false);

      try {
        const docRef = doc(db, 'users', targetUserId, 'symbolicMetrics', 'weekly_radar_scores');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setRadarData(docSnap.data() as WeeklyRadarScore);
        } else {
          setRadarData(generateSyntheticData());
        }
      } catch (err) {
        console.error("Error fetching radar data:", err);
        setError("Could not load symbolic metrics.");
      } finally {
        setLoading(false);
      }
    };

    if (!enableConsent || settings) {
      fetchRadarData();
    }
  }, [user, patientId, settings, enableConsent]);

  return (
    <RadarChartContext.Provider value={{ 
      radarData, 
      loading, 
      error, 
      isPatient, 
      requiresConsent, 
      grantConsent 
    }}>
      {children}
    </RadarChartContext.Provider>
  );
};

export default RadarChartProvider; 